import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();

    // 🔥 Disable native summary toggle & control manually
    const summary = this.querySelector('summary');

    if (summary) {
      summary.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggle();
      });
    }

    // 🔥 Optional: close on backdrop click
    this.addEventListener('click', (e) => {
      if (
        this.isOpen &&
        e.target.matches('[data-header-drawer]')
      ) {
        this.close();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * ESC key closes drawer
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;
    this.close();
  };

  /**
   * Check if open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Toggle drawer
   */
  toggle() {
    const details = this.refs.details;

    if (this.isOpen) {
      this.close();
    } else {
      details.setAttribute('open', true); // ✅ required
      this.open();
    }
  }

  /**
   * Open drawer
   */
  open() {
    const details = this.refs.details;
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      setTimeout(() => {
        trapFocus(details);
      }, 0);
    });
  }

  /**
   * Close drawer
   */
  close() {
    const details = this.refs.details;
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      this.#reset(details);
      removeTrapFocus();
    });
  }

  /**
   * Reset state
   */
  #reset(details) {
    details.removeAttribute('open'); // ✅ now safe (JS controlled)
    details.classList.remove('menu-open');

    const summary = details.querySelector('summary');
    summary?.setAttribute('aria-expanded', 'false');
  }

  /**
   * Animation cleanup
   */
  #setupAnimatedElementListeners() {
    const elements = this.querySelectorAll('.menu-drawer__animated-element');

    elements.forEach((el) => {
      el.addEventListener('animationend', function handler(e) {
        e.target.style.willChange = 'unset';
        e.target.removeEventListener('animationend', handler);
      });
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}